import React, { useCallback, useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import isNumeric from '../../../utils/is-numeric';
import keycodes from '../../constants/key-codes';
import { ALIGN } from '../input-group/constants';

const ValueInput = ({
  value = 0,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  align = ALIGN.right,
  fullWidth = false,
  noBackground = false,
  disabled = false,
  onChange,
  onBlur = Function.prototype,
}) => {
  const [lastValue, setLastValue] = useState(value);
  const [inputValue, setInputValue] = useState(value);

  const handleChange = useCallback(
    (target) => {
      const numberValue = isNumeric(Number(target.value)) ? Number(target.value) : lastValue;
      const restrictedValue = Math.round(Math.min(Math.max(numberValue, min), max));

      setLastValue(restrictedValue);
      setInputValue(restrictedValue);
      onChange(restrictedValue);
    },
    [lastValue, max, min, onChange]
  );

  const handleBlur = useCallback(
    ({ target }) => {
      handleChange(target);
      onBlur();
    },
    [handleChange, onBlur]
  );

  const handleKeyDown = useCallback(
    ({ target, keyCode }) => {
      if (keyCode === keycodes.ENTER) {
        handleChange(target);
      }
    },
    [handleChange]
  );

  const handleInputChange = useCallback(({ target }) => {
    setInputValue(target.value);
  }, []);

  const classes = classNames('ws-ui-value-input', {
    'ws-ui-value-input--disabled': disabled,
    'ws-ui-value-input--full-width': fullWidth,
    'ws-ui-value-input--no-background': noBackground,
  });

  const inputClasses = classNames(
    'ws-ui-value-input__input',
    `ws-ui-value-input__input--align-${align}`
  );

  useEffect(() => {
    setLastValue(value);
    setInputValue(value);
  }, [value]);

  return (
    <div className={classes}>
      <input
        className={inputClasses}
        value={inputValue}
        disabled={disabled}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

ValueInput.propTypes = {
  value: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  fullWidth: PropTypes.bool,
  noBackground: PropTypes.bool,
  align: PropTypes.oneOf(Object.keys(ALIGN)),
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
};

export default ValueInput;
